import { useCallback, useMemo } from 'react';
import { PersonneSelect } from '../PersonneSelect';
import { User } from 'lucide-react';
import { TexteDiffere } from './TexteDiffere';
import type { DateIso } from './dates';
import { CarteTipee } from './CarteTipee';
import { FOCUS_ATTR } from './constantes';
import { JourDuPanneau } from './JourDuPanneau';
import { LignesPeriodes } from './LignesPeriodes';
import { ResteAFaire, type ItemReste } from './ResteAFaire';
import { dePrenom, formatHeures, pluriel } from './textesDemande';
import type { Seance } from './seancesDemande';
import type { Nd } from './useNouvelleDemande';

interface Props {
  nd: Nd;
  ouverte: string | null;
  onOuvrirSeance: (s: Seance, ou: 'c' | 'p') => void;
  /** Ouvre la fenêtre d'une séance précise (« garant·e du cadre » manquant). */
  onOuvrirParCle: (cle: string) => void;
  onEnvoyer: () => void;
}

/** Le panneau « Ma demande » : se remplit en direct, dit ce qu'il reste à faire et pourquoi « Envoyer » n'est pas encore possible. */
export function PanneauDemande({ nd, ouverte, onOuvrirSeance, onOuvrirParCle, onEnvoyer }: Props) {
  const { etat, dispatch, compteurs, manques, solde, dire, pourMoi, personneAbsente, peutChoisirAutrui, reglages, envoi, erreur, donnees } = nd;
  const prenom = personneAbsente?.prenom ?? '';
  // Le nom de la personne absente pour la carte Tipee : null = « vous » ; '' = pas encore choisie.
  const nomAbsentTiers = pourMoi ? null : (personneAbsente?.display_name ?? '');
  const pret = donnees.etat === 'pretes';   // les séances des jours choisis sont toutes connues : sinon on ne compte rien

  // ── « Il reste à faire » ───────────────────────────────────────────────────────────────────────────
  const items: ItemReste[] = useMemo(() => {
    const cles = new Set(manques.map((m) => m.cle));
    const r: ItemReste[] = [];
    if (peutChoisirAutrui) r.push({ cle: 'absent', ok: !cles.has('absent'), txt: etat.absentId != null ? `Absence ${dePrenom(personneAbsente?.display_name ?? '')}` : 'Personne absente' });
    const nj = etat.jours.length;
    r.push({ cle: 'dates', ok: !cles.has('dates') && nj > 0, txt: nj ? `${pluriel(nj, 'jour')}${pret ? ` · ${pluriel(compteurs.total, 'séance')}` : ''}` : "Jours d'absence" });
    if (cles.has('garant')) r.push({ cle: 'garant', ok: false, txt: 'Garant·e du cadre' });
    r.push({ cle: 'motif', ok: etat.motif != null, txt: etat.motif ? `Motif : ${etat.motif === 'vacances' ? 'Vacances' : 'Autre motif'}` : "Motif de l'absence" });
    r.push({ cle: 'tipee', ok: etat.tipee, txt: etat.tipee ? `Tipee : ${peutChoisirAutrui && !pourMoi ? 'vérifié' : 'déjà fait'}` : 'Rappel Tipee' });
    return r;
  }, [manques, peutChoisirAutrui, etat.absentId, etat.jours.length, etat.motif, etat.tipee, compteurs.total, personneAbsente, pourMoi, pret]);
  const faits = items.filter((i) => i.ok).length;

  const flash = useCallback((id: string) => {
    const n = document.getElementById(`nd-sec-${id}`);
    if (!n) return;
    const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    n.scrollIntoView({ block: 'start', behavior: reduit ? 'auto' : 'smooth' });
    n.classList.add('is-flash');
    setTimeout(() => n.classList.remove('is-flash'), 1600);
  }, []);
  const focaliser = (sel: string) => document.querySelector<HTMLElement>(sel)?.focus({ preventScroll: true });
  /** Le focus va aux dates : à la case du calendrier quand il est à côté du panneau ; en une colonne la case est loin au-dessus (hors écran), on vise le champ « Du », sous les yeux. */
  const focaliserDates = () => focaliser(document.querySelector('.nd--etroit') ? `[${FOCUS_ATTR}="pdun"], [${FOCUS_ATTR}="perAjout"]` : '.nd-jour[tabindex="0"]');
  const aller = useCallback((cle: string) => {
    const m = manques.find((x) => x.cle === cle);
    if (cle === 'dates') { focaliserDates(); flash('dates'); if (m) dispatch({ type: 'message', texte: m.txt }); return; }
    if (cle === 'garant' && m?.cleSeance) { onOuvrirParCle(m.cleSeance); return; }
    flash(cle);
    const cible = { absent: '#nd-absent', motif: '[data-motif]', tipee: `[${FOCUS_ATTR}="tipeeFait"], [${FOCUS_ATTR}="tipeeAnnuler"]` }[cle as 'absent' | 'motif' | 'tipee'];
    if (cible) focaliser(cible);
  }, [manques, flash, dispatch, onOuvrirParCle]);

  const envoyables = compteurs.rech + compteurs.trouve;
  const sections = useMemo(() => [...etat.jours].sort(), [etat.jours]);

  return (
    <section className="nd-panneau" aria-labelledby="nd-panneau-titre">
      <div className="nd-panneau-corps" id="nd-panneau-corps">
        <div className="nd-panneau-tete">
          <h2 className="nd-panneau-titre" id="nd-panneau-titre" tabIndex={-1}>{pourMoi || !peutChoisirAutrui ? 'Ma demande' : 'La demande'}</h2>
          <span className="text-xs text-ink-3">{faits} / {items.length} fait</span>
        </div>
        <ResteAFaire items={items} onAller={aller} />

        {/* Personne absente */}
        <section className="nd-sec" id="nd-sec-absent" aria-labelledby="nd-h-absent">
          <h3 className="nd-h" id="nd-h-absent">{peutChoisirAutrui ? 'Qui est absent·e ?' : 'Personne absente'}</h3>
          {peutChoisirAutrui ? (
            <>
              <PersonneSelect
                id="nd-absent" ariaLabelledby="nd-h-absent" value={etat.absentId} filtres={{ actif: true }} required
                onChange={(id) => dispatch({ type: 'absent', id, nom: id != null ? nd.personnesParId[id]?.display_name ?? '' : '' })}
              />
              <p className="nd-aide">Ses séances apparaissent sur les jours choisis.</p>
            </>
          ) : nd.maPersonne ? (
            <>
              <div className="nd-verrou"><User size={16} aria-hidden="true" /><strong>{nd.maPersonne.display_name}</strong></div>
              <p className="nd-aide">La demande porte sur votre propre absence.</p>
            </>
          ) : nd.chargementMoi ? (
            <p className="nd-aide">Chargement de votre fiche…</p>
          ) : (
            <p className="nd-alerte">Votre compte n'est relié à aucune fiche intervenant·e : contactez le secrétariat pour pouvoir créer une demande.</p>
          )}
        </section>

        {/* Quand ? */}
        <section className="nd-sec" id="nd-sec-dates" aria-labelledby="nd-h-dates">
          <h3 className="nd-h" id="nd-h-dates">Quand ?</h3>
          <LignesPeriodes nd={nd} />
          {etat.jours.length > 0 && (
            <div className="nd-ligne-boutons">
              <button type="button" className="btn btn-sm btn-ghost" data-fid="effacer" onClick={() => { dispatch({ type: 'effacer' }); nd.apresRendu(focaliserDates); }}>
                Effacer les jours
              </button>
            </div>
          )}
          <label className="nd-case">
            <input type="checkbox" checked={etat.flex} data-fid="flex" onChange={(e) => dispatch({ type: 'flex', oui: e.target.checked })} />
            <span>
              <strong>Dates flexibles</strong>{' '}
              {dire(
                "— je peux décaler mes dates d'absence si aucun·e remplaçant·e n'est trouvé·e. L'équipe gestion le verra sur la demande.",
                prenom ? `— ${prenom} peut décaler ses dates si aucun·e remplaçant·e n'est trouvé·e. Affiché sur la demande.`
                  : "— les dates d'absence peuvent être décalées si aucun·e remplaçant·e n'est trouvé·e. Affiché sur la demande.",
              )}
            </span>
          </label>
        </section>

        {/* Séances */}
        <section className="nd-sec" id="nd-sec-seances" aria-labelledby="nd-h-seances">
          <h3 className="nd-h" id="nd-h-seances">
            Séances
            {etat.jours.length > 0 && pret && (
              <span className="nd-cpts">
                {compteurs.trouve > 0 && <span className="badge badge-ok">{pluriel(compteurs.trouve, 'trouvé·e', 'trouvé·e·s')}</span>}
                {compteurs.rech > 0 && <span className="badge">{compteurs.rech} à rechercher</span>}
                {compteurs.aEnvoyer > 0 && <span className="badge badge-warn">{compteurs.aEnvoyer} à envoyer</span>}
                {compteurs.deja - compteurs.aEnvoyer > 0 && <span className="badge badge-warn">{compteurs.deja - compteurs.aEnvoyer} déjà demandé{compteurs.deja - compteurs.aEnvoyer > 1 ? 'es' : 'e'}</span>}
              </span>
            )}
          </h3>
          {etat.jours.length === 0 ? (
            <p className="nd-aide">{dire("Vos séances s'afficheront ici, jour par jour.", prenom ? `Les séances ${dePrenom(prenom)} s'afficheront ici, jour par jour.` : "Les séances s'afficheront ici, jour par jour.")}</p>
          ) : (
            <>
              <p className="nd-aide">
                {dire(
                  "Par défaut, chaque séance est « À rechercher » : un message pour vos collègues est préparé à l'envoi. C'est à vous de chercher en premier ; si personne n'est trouvé, l'équipe gestion vous aidera.",
                  `Par défaut « À rechercher » : un message pour les collègues est préparé à l'envoi. ${prenom ? `${prenom} cherche en premier ; l'équipe gestion` : "L'équipe gestion"} prend le relais si personne n'est trouvé·e.`,
                )}
              </p>
              {sections.map((d: DateIso) => <JourDuPanneau key={d} nd={nd} date={d} ouverte={ouverte} onOuvrirSeance={onOuvrirSeance} />)}
            </>
          )}
        </section>

        {/* Motif et solde */}
        <section className="nd-sec" id="nd-sec-motif" aria-labelledby="nd-h-motif">
          <h3 className="nd-h" id="nd-h-motif">Motif de l'absence <span className="nd-requis">obligatoire</span></h3>
          <div className="nd-motif" role="radiogroup" aria-labelledby="nd-h-motif" aria-required="true"
            onKeyDown={(e) => {
              if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
              e.preventDefault();
              const v = etat.motif === 'vacances' ? 'autre' : 'vacances';
              dispatch({ type: 'motif', motif: v });
              nd.apresRendu(() => focaliser(`[data-motif="${v}"]`));
            }}>
            {([['vacances', 'Vacances'], ['autre', 'Autre motif (formation, rendez-vous, maladie…)']] as const).map(([v, l]) => (
              <button key={v} type="button" role="radio" className={`selectable${etat.motif === v ? ' active' : ''}`} aria-checked={etat.motif === v}
                data-motif={v} tabIndex={etat.motif === v || (etat.motif == null && v === 'vacances') ? 0 : -1} onClick={() => dispatch({ type: 'motif', motif: v })}>
                {l}
              </button>
            ))}
          </div>
          {etat.motif === 'vacances' && solde?.verifie && (
            <p className={`nd-solde${solde.hors_solde ? ' is-warn' : ''}`}>
              <span>
                Solde de vacances dans Tipee : <strong>{formatHeures(solde.solde_heures)}</strong> · cette absence : <strong>{formatHeures(solde.heures_demandees)}</strong>
                {solde.hors_solde && ' — au-delà du solde (vous pourrez quand même envoyer).'}
              </span>
            </p>
          )}
        </section>

        {/* Tipee */}
        <section className="nd-sec" id="nd-sec-tipee" aria-labelledby="nd-h-tipee">
          <h3 className="nd-h" id="nd-h-tipee">Tipee <span className="nd-requis">obligatoire</span></h3>
          <CarteTipee tipeeUrl={reglages.tipeeUrl} confirme={etat.tipee} ouvert={etat.tipeeOuvert} onOuvert={() => dispatch({ type: 'tipeeOuvert' })}
            onConfirmer={(ok) => { dispatch({ type: 'tipee', ok }); nd.apresRendu(() => focaliser(ok ? `[${FOCUS_ATTR}="tipeeAnnuler"]` : `[${FOCUS_ATTR}="tipeeFait"]`)); }} nomAbsent={nomAbsentTiers} />
        </section>

        {/* Précisions */}
        <section className="nd-sec" id="nd-sec-notes" aria-labelledby="nd-h-notes">
          <h3 className="nd-h" id="nd-h-notes">
            {pourMoi || !peutChoisirAutrui ? 'Précisions pour vos collègues' : prenom ? `Précisions pour les collègues ${dePrenom(prenom)}` : 'Précisions pour les collègues'} <span className="nd-requis nd-facult">facultatif</span>
          </h3>
          <TexteDiffere
            multiligne id="nd-notes" className="form-input nd-notes" rows={3} aria-labelledby="nd-h-notes" aria-describedby="nd-notes-aide" placeholder="Matériel, clés, consignes du groupe…"
            valeur={etat.notes} onValider={(texte) => dispatch({ type: 'notes', texte })}
          />
          <p className="nd-aide" id="nd-notes-aide">Recopiées dans le message pour les collègues (Slack) et, si l'équipe gestion élargit la recherche, dans le message envoyé à des personnes extérieures. N'y indiquez aucune information médicale ou personnelle.</p>
        </section>
      </div>

      {/* Pied : ce qui manque, et le bouton qui le dit */}
      <div className="nd-panneau-pied">
        <div className="nd-pied-txt" id="nd-pourquoi" aria-live="polite">
          {erreur ? <span className="nd-erreur">{erreur}</span> : manques.length ? (
            <span>{manques[0].txt}{manques.length > 1 ? ` (encore ${manques.length - 1} à régler)` : ''}</span>
          ) : (
            <span>
              Prêt : {pluriel(envoyables, 'demande')} à créer
              {compteurs.deja - compteurs.aEnvoyer > 0 ? `, ${pluriel(compteurs.deja - compteurs.aEnvoyer, 'déjà demandée')}` : ''}
              {compteurs.aEnvoyer > 0 ? `, ${compteurs.aEnvoyer} à envoyer (repérée${compteurs.aEnvoyer > 1 ? 's' : ''} dans Tipee)` : ''}.
            </span>
          )}
        </div>
        <button type="button" className="btn btn-accent nd-envoyer" id="nd-envoyer" data-fid="envoyer" aria-describedby="nd-pourquoi" aria-disabled={manques.length > 0 || envoi || undefined}
          onClick={() => {
            if (envoi) return;
            if (manques[0]?.cle === 'donnees') { dispatch({ type: 'message', texte: manques[0].txt }); return; }   // rien à faire qu'attendre (ou « Réessayer »)
            if (manques.length) { aller(manques[0].cle); dispatch({ type: 'message', texte: `Pour envoyer : ${manques.map((m) => m.txt.replace(/\.$/, '')).join(' · ')}.` }); return; }
            onEnvoyer();
          }}>
          {envoi ? <span className="spinner spinner-sm" aria-label="Envoi en cours" /> : (pourMoi || !peutChoisirAutrui ? 'Envoyer ma demande' : 'Envoyer la demande')}
        </button>
      </div>
    </section>
  );
}
